// A small popover menu on a chip, rendered in a portal at the trigger's screen
// position so a row's hover or click can never swallow it. Modelled on Orbit's
// TaskMenu: click the chip, pick a value, the row patches in place.
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

/** Gap kept between a menu and the edge of the window. */
const EDGE = 8;

export default function QuickMenu({
  trigger,
  children,
  align = "left",
  label,
  disabled,
}: {
  trigger: ReactNode;
  /** The menu body; call close() after a pick. */
  children: (close: () => void) => ReactNode;
  align?: "left" | "right";
  /** What the chip sets, for screen readers: "Due date for Follow up with…". */
  label: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number }>({ top: 0 });

  const toggle = (e: MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    if (open) return setOpen(false);
    const r = btn.current?.getBoundingClientRect();
    if (r) {
      // A first guess (upwards when there is no room below); the layout effect
      // corrects it with the menu's real size before anything paints.
      const below = window.innerHeight - r.bottom;
      const top = below < 260 ? Math.max(EDGE, r.top - EDGE - 240) : r.bottom + 4;
      setPos(align === "right" ? { top, right: Math.max(EDGE, window.innerWidth - r.right) } : { top, left: Math.min(r.left, window.innerWidth - 240) });
    }
    setOpen(true);
  };
  // Once open, measure the menu and keep all of it on screen: the date menu is wider
  // than the 240px guess and opened past the right edge from a table's last columns.
  useLayoutEffect(() => {
    if (!open) return;
    const r = btn.current?.getBoundingClientRect();
    const box = menu.current?.getBoundingClientRect();
    if (!r || !box) return;
    // clientWidth leaves out the page scrollbar, which a menu must not sit under.
    const width = document.documentElement.clientWidth || window.innerWidth;
    const height = window.innerHeight;
    const wanted = align === "right" ? r.right - box.width : r.left;
    const left = Math.max(EDGE, Math.min(wanted, width - EDGE - box.width));
    // Below the trigger when it fits, else above it, else as low as the window allows.
    const top = r.bottom + 4 + box.height <= height - EDGE ? r.bottom + 4
      : r.top - 4 - box.height >= EDGE ? r.top - 4 - box.height
        : Math.max(EDGE, height - EDGE - box.height);
    setPos({ top, left });
  }, [open, align]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); btn.current?.focus(); }
    };
    // The page scrolling moves the trigger away, so the menu closes; scrolling a long
    // menu itself (the LSA Show list) must not, or its lower options are out of reach.
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && menu.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("scroll", onScroll, true); };
  }, [open]);

  return (
    <span className="qmenu-anchor" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button ref={btn} type="button" className={`qmenu-trigger${disabled ? " is-static" : ""}`} aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={toggle} disabled={disabled}>
        {trigger}
      </button>
      {open && createPortal(
        <>
          <div className="qmenu-backdrop" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div ref={menu} className="qmenu" role="menu" aria-label={label} style={{ top: pos.top, left: pos.left, right: pos.right }} onClick={(e) => e.stopPropagation()}>
            {children(() => setOpen(false))}
          </div>
        </>,
        document.body,
      )}
    </span>
  );
}

export function QuickMenuItem({ children, onClick, active, tone }: {
  children: ReactNode;
  onClick: () => void;
  active?: boolean;
  tone?: "danger";
}) {
  return (
    <button type="button" role="menuitemradio" aria-checked={!!active} className={`qmenu-item${active ? " active" : ""}${tone ? ` ${tone}` : ""}`} onClick={onClick}>
      <span className="qmenu-item-body">{children}</span>
      {active && <Check aria-hidden="true" />}
    </button>
  );
}
